import React, {useRef, useState} from 'react'
import _ from 'lodash'
import dayjs from 'dayjs'
import classNames from 'classnames'
import {Button, Checkbox, Input, InputNumber, message, Select} from 'antd'
import {FaFileExport, FaFileImport} from 'react-icons/fa6'
import {IoRefreshOutline} from 'react-icons/io5'
import useAsync from 'modules/asyncCache/useAsync'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import {callApi} from 'modules/asyncCache/callApi'
import {bindQueryParams} from 'views/AppLocals/functions/routerHelper'
import {getResponseItems} from 'views/Shared'
import LoadingBlock from 'views/Loading/LoadingBlock'
import {
    familyBook_getFamilyBooksByParish_Api,
    familyBookImportExport_applyImport_Api,
    familyBookImportExport_exportTemplate_Api,
    familyBookImportExport_previewImport_Api,
    familyBookMember_fetchFamilyBookMembersByParish_Api,
} from 'apis/familyBook'
import {ALL_SACRAMENT_TYPES, SACRAMENT_LABELS} from '../../AppLocals/FamilyBook/components/MemberDetailModal'
import {InfoBanner} from '../../AppLocals/FamilyBook/components/MomentRow'
import {IMPORT_EXPORT_ENTITIES, IMPORT_EXPORT_ENTITY_ORDER} from './import_export_config'

// "Xuất/Nhập hàng loạt" GENERIC cho cả 3 loại (Sổ/Thành viên/Bí tích, xem import_export_config.js +
// config/family_book_import_export.php phía BE - key phải khớp NHAU 2 bên) - THAY HẲN luồng "Nhập
// File Sổ Gia Đình" Excel cũ (importFamilyBookFile, đã bỏ - GEDCOM (.ged) riêng vẫn giữ nguyên, xem
// GedcomImportCard trong containers/FamilyBooks.js, KHÁC phạm vi hẳn - nhập cả cây phả hệ nhiều đời
// từ file chuẩn genealogy, không phải luồng bulk theo cặp vợ chồng này).
// CHỈ admin giáo xứ dùng được (BE tự chặn qua organization_manage_sacrament + org đã liên kết giáo
// xứ - route Workspace family-book cũng đã gate permission này ở Workspace.js).
const SUB_TABS = {EXPORT: 'export', IMPORT: 'import'}

// ─── Tab Xuất - UI đổi theo exportMode của entity đang chọn ────────────────────────────────────────
const BlankRowsExportPanel = ({entity, owner_id, owner_type, t}) => {
    const [rowCount, setRowCount] = useState(10)

    const {handleAsyncAction: doExport, loading: exporting} = useAsyncAction({
        apiInfo: familyBookImportExport_exportTemplate_Api,
        onSuccess: (result, {response}) => {
            const url = _.get(response, 'data.url')
            if (url) window.open(url, '_blank')
            message.success(t('import export template exported'))
        },
        onError: (err) => message.error(_.get(err, 'response.data.errors.message', t('error occurred'))),
    })

    return (
        <div className="flex flex-col gap-3">
            <InfoBanner>{t('import export blank export hint')}</InfoBanner>
            <div className="flex flex-col gap-1.5">
                <span className="text-xs font-bold uppercase tracking-widest text-color-300">{t('row count to create')}</span>
                <InputNumber size="large" className="w-full" min={1} max={500} value={rowCount} onChange={setRowCount}/>
            </div>
            <Button
                type="primary" size="large" icon={<FaFileExport size={13}/>} loading={exporting}
                onClick={() => doExport({row_count: rowCount}, {type: owner_type, id: owner_id, key: entity.key})}>
                {t('export blank template')}
            </Button>
        </div>
    )
}

const PickListExportPanel = ({entity, owner_id, owner_type, t}) => {
    const isMemberPick = entity.exportMode === 'pick_member'
    const [searchKeyword, setSearchKeyword] = useState('')
    const [selectedIds, setSelectedIds] = useState([])
    const [sacramentType, setSacramentType] = useState(ALL_SACRAMENT_TYPES[0])
    // Bí tích: mặc định ẨN người đã có sẵn loại đang chọn - tránh chọn nhầm lại người vừa nhập đợt
    // trước. Con cái: mặc định KHÔNG ẩn sổ đã có con (khác bí tích - 1 sổ có thể cần thêm con nhiều
    // đợt, "đã có con" không có nghĩa là "xong rồi").
    const [hideAlreadyDone, setHideAlreadyDone] = useState(isMemberPick)

    const {response, isLoading, handleAsyncAction: refetch} = useAsync({
        apiInfo: isMemberPick ? familyBookMember_fetchFamilyBookMembersByParish_Api : familyBook_getFamilyBooksByParish_Api,
        query: bindQueryParams([{type: owner_type}, {id: owner_id}]),
        values: {
            keyword: searchKeyword,
            ...(isMemberPick
                ? {exclude_sacrament_type: hideAlreadyDone ? sacramentType : undefined}
                : {exclude_has_children: hideAlreadyDone ? 1 : undefined}),
        },
        deps: [owner_id, owner_type, searchKeyword, isMemberPick, hideAlreadyDone, sacramentType],
    })
    // Thêm Thành viên - CHỈ cho chọn sổ org TRỰC TIẾP SỞ HỮU (is_owned_by_viewer), không phải mọi sổ
    // liên kết giáo xứ - đúng ranh giới quyền canEditFamilyBook() đã chốt (org không tự thêm thành
    // viên vào sổ cá nhân giáo dân dù có liên kết), BE cũng tự re-check lại phạm vi này lúc Nhập.
    const items = isMemberPick ? getResponseItems(response) : getResponseItems(response).filter((b) => b.is_owned_by_viewer)

    const toggle = (id) => setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])

    const {handleAsyncAction: doExport, loading: exporting} = useAsyncAction({
        apiInfo: familyBookImportExport_exportTemplate_Api,
        onSuccess: (result, {response}) => {
            const url = _.get(response, 'data.url')
            const total = _.get(response, 'data.total', 0)
            if (url) window.open(url, '_blank')
            // total=0 - mục đã chọn không khớp phạm vi giáo xứ (BE vẫn xuất file mẫu TRỐNG thay vì
            // chặn lỗi, xem FamilyBookImportExportController::exportTemplate()) - báo rõ để user biết
            // vì sao file tải về không có dòng nào, tránh tưởng nhầm là lỗi hệ thống.
            if (total === 0) message.warning(t('import export template exported empty'))
            else message.success(t('import export template exported'))
            setSelectedIds([])
        },
        onError: (err) => message.error(_.get(err, 'response.data.errors.message', t('error occurred'))),
    })

    return (
        <div className="flex flex-col gap-3">
            <InfoBanner>{t(isMemberPick ? 'sacrament bulk export hint' : 'import export pick family book hint')}</InfoBanner>

            {entity.needsSacramentTypeSelect && (
                <Select
                    size="large"
                    value={sacramentType}
                    onChange={setSacramentType}
                    options={ALL_SACRAMENT_TYPES.map((type) => ({value: type, label: t(SACRAMENT_LABELS[type])}))}
                />
            )}

            <div className="flex items-center gap-2">
                <Input.Search
                    size="large" allowClear
                    className="flex-1"
                    placeholder={t(isMemberPick ? 'search by name' : 'search family book by title')}
                    onSearch={(v) => setSearchKeyword(v)}
                />
                <button
                    type="button" onClick={() => refetch()} title={t('refresh')}
                    className="w-10 h-10 flex-shrink-0 flex flex-center rounded-lg border border-color-200 text-color-400 hover:text-family-700 hover:border-family-300 cursor-pointer">
                    <IoRefreshOutline size={17}/>
                </button>
            </div>
            <Checkbox checked={hideAlreadyDone} onChange={(e) => setHideAlreadyDone(e.target.checked)}>
                <span className="text-xs text-color-400">
                    {t(isMemberPick ? 'hide members already have this sacrament' : 'hide family books already have children')}
                </span>
            </Checkbox>

            <div className="flex flex-col gap-2 max-h-96 overflow-y-auto">
                {isLoading && <div className="py-6"><LoadingBlock/></div>}
                {!isLoading && items.length === 0 && (
                    <span className="text-sm text-color-300 italic text-center py-6">{t('no results found')}</span>
                )}
                {items.map((item) => (
                    <label
                        key={item.id}
                        className="p-2.5 flex items-center gap-2.5 rounded-lg border border-color-100 hover:bg-color-50 cursor-pointer">
                        <Checkbox checked={selectedIds.includes(item.id)} onChange={() => toggle(item.id)}/>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-color-000 truncate">
                                {isMemberPick ? item.name : item.title}
                            </div>
                            <div className="text-xs text-color-400 truncate">
                                {isMemberPick
                                    ? [item.birth_date ? dayjs(item.birth_date).format('DD/MM/YYYY') : null, item.family_book?.title].filter(Boolean).join(' · ')
                                    : [item.head_member?.name, item.head_spouse_member?.name].filter(Boolean).join(' & ')}
                            </div>
                        </div>
                    </label>
                ))}
            </div>

            <div className="sticky bottom-2 p-3 flex flex-col gap-2 background border border-color-100 rounded-xl shadow-md">
                <span className="text-sm font-semibold text-color-000">{t('selected count')}: {selectedIds.length}</span>
                <Button
                    type="primary" size="large" icon={<FaFileExport size={13}/>} loading={exporting}
                    disabled={!selectedIds.length}
                    onClick={() => doExport(
                        // JSON.stringify - field mảng đi qua useAsyncAction (route qua createFormData(),
                        // FormData.append KHÔNG serialize array) PHẢI stringify tường minh, đúng
                        // convention docs/coding-style/conventions.md "FormData — array/object field
                        // phải JSON.stringify" (đã dùng cho timetable_details/content_data) - BE
                        // json_decode lại ở ImportExportSupport::decodeArrayParam().
                        isMemberPick
                            ? {member_ids: JSON.stringify(selectedIds), sacrament_type: sacramentType}
                            : {family_book_ids: JSON.stringify(selectedIds)},
                        {type: owner_type, id: owner_id, key: entity.key}
                    )}>
                    {t('export selected template')}
                </Button>
            </div>
        </div>
    )
}

const ExportTab = ({entity, owner_id, owner_type, t}) => {
    if (entity.exportMode === 'blank') return <BlankRowsExportPanel entity={entity} owner_id={owner_id} owner_type={owner_type} t={t}/>
    return <PickListExportPanel entity={entity} owner_id={owner_id} owner_type={owner_type} t={t}/>
}

// ─── Tab Nhập - GENERIC (upload -> preview -> apply), chỉ phần TÓM TẮT 1 dòng preview khác nhau
// theo entity (payload mỗi loại khác hẳn nhau) ───────────────────────────────────────────────────
const PREVIEW_ROW_SUMMARY = {
    family_book: (row, t) => row.title || t('not determined yet'),
    family_book_member: (row, t) => [row.after?.name, row.family_book?.title].filter(Boolean).join(' · '),
    sacrament: (row, t) => [row.member?.name, t(SACRAMENT_LABELS[row.sacrament_type] || row.sacrament_type)].filter(Boolean).join(' · '),
}
const PREVIEW_ROW_DETAIL = {
    family_book: (row, t) => [
        row.after?.husband_birth_date ? `${t('husband')}: ${dayjs(row.after.husband_birth_date).format('DD/MM/YYYY')}` : null,
        row.after?.wife_birth_date ? `${t('wife')}: ${dayjs(row.after.wife_birth_date).format('DD/MM/YYYY')}` : null,
        row.after?.source_idcode,
    ].filter(Boolean).join(' · '),
    family_book_member: (row, t) => [
        row.after?.birth_date ? dayjs(row.after.birth_date).format('DD/MM/YYYY') : null,
        row.after?.patron_saint_name,
    ].filter(Boolean).join(' · '),
    sacrament: (row, t) => [
        row.after?.eff_date ? dayjs(row.after.eff_date).format('DD/MM/YYYY') : t('not provided yet'),
        row.after?.church_name,
    ].filter(Boolean).join(' · '),
}

const ImportRowCard = ({entityKey, row, t}) => {
    const ok = row.status === 1
    return (
        <div className={classNames('p-2.5 flex flex-col gap-1 rounded-lg border', ok ? 'border-color-100' : 'border-rose-200 bg-rose-50')}>
            <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold text-color-000 truncate">
                    {ok ? (PREVIEW_ROW_SUMMARY[entityKey]?.(row, t) || `#${row.row_number}`) : `#${row.row_number}`}
                </span>
                <span className={classNames('px-2 py-0.5 rounded-md text-2xs font-bold flex-shrink-0', ok ? 'bg-family-50 text-family-700' : 'bg-rose-100 text-rose-700')}>
                    {ok ? t('valid') : t('error')}
                </span>
            </div>
            {!ok && <span className="text-xs text-rose-600">{row.error_message}</span>}
            {ok && <span className="text-xs text-color-400">{PREVIEW_ROW_DETAIL[entityKey]?.(row, t)}</span>}
        </div>
    )
}

const ImportTab = ({entity, owner_id, owner_type, t}) => {
    const inputRef = useRef(null)
    const [uploading, setUploading] = useState(false)
    const [preview, setPreview] = useState(null)

    // callApi() trực tiếp (KHÔNG qua useAsyncAction) - ngoại lệ upload file đã chốt.
    const handleFile = (file) => {
        if (!file || uploading) return
        setUploading(true)
        callApi({
            apiInfo: familyBookImportExport_previewImport_Api,
            query: bindQueryParams([{type: owner_type}, {id: owner_id}, {key: entity.key}]),
            values: {file},
        }).then((result) => {
            setUploading(false)
            if (inputRef.current) inputRef.current.value = ''
            if (!result) return
            if (result.error) {
                message.error(_.get(result, 'errorMessages.error') || t('error occurred'))
                return
            }
            setPreview(result.result)
        })
    }

    const {handleAsyncAction: doApply, loading: applying} = useAsyncAction({
        apiInfo: familyBookImportExport_applyImport_Api,
        onSuccess: (result, {response}) => {
            const data = _.get(response, 'data')
            message.success(`${t('import export applied')}: ${data?.applied ?? 0}${data?.failed ? ` (${t('error')}: ${data.failed})` : ''}`)
            setPreview(null)
        },
        onError: (err) => message.error(_.get(err, 'response.data.errors.message', t('error occurred'))),
    })

    if (!preview) {
        return (
            <div className="flex flex-col gap-3">
                <InfoBanner>{t('import export upload hint')}</InfoBanner>
                <label className="p-8 flex flex-col items-center gap-2 rounded-xl border-2 border-dashed border-family-300 hover:bg-family-50 cursor-pointer transition-all">
                    <FaFileImport size={26} className="text-family-600"/>
                    <span className="text-sm font-semibold text-color-000">{uploading ? t('uploading') : t('choose file to upload')}</span>
                    <input ref={inputRef} type="file" accept=".xlsx,.xls" className="hidden" disabled={uploading}
                           onChange={(e) => handleFile(e.target.files?.[0])}/>
                </label>
            </div>
        )
    }

    const validCount = preview.rows.filter((r) => r.status === 1).length

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold text-color-000">
                    {t('import export summary')}: {preview.valid}/{preview.total}
                </span>
                <button type="button" onClick={() => setPreview(null)} className="text-xs text-family-700 underline cursor-pointer flex-shrink-0">
                    {t('choose another file')}
                </button>
            </div>

            <div className="flex flex-col gap-2 max-h-96 overflow-y-auto">
                {preview.rows.map((row) => <ImportRowCard key={row.id} entityKey={entity.key} row={row} t={t}/>)}
            </div>

            <Button
                type="primary" size="large" loading={applying} disabled={!validCount}
                onClick={() => doApply({batch_id: preview.batch_id}, {type: owner_type, id: owner_id, key: entity.key})}>
                {t('apply import')} ({validCount})
            </Button>
        </div>
    )
}

// ─── Root ────────────────────────────────────────────────────────────────────────────────────────
const FamilyBookImportExportPage = ({owner_id, owner_type, t}) => {
    const [entityKey, setEntityKey] = useState(IMPORT_EXPORT_ENTITY_ORDER[0])
    const [subTab, setSubTab] = useState(SUB_TABS.EXPORT)
    const entity = IMPORT_EXPORT_ENTITIES[entityKey]

    return (
        <div className="flex flex-col gap-3">
            <div className="overflow-x-auto">
                <div className="p-1 bg-family-50 rounded-xl flex items-center gap-2 w-fit">
                    {IMPORT_EXPORT_ENTITY_ORDER.map((key) => (
                        <button
                            key={key} type="button"
                            onClick={() => {
                                setEntityKey(key)
                                setSubTab(SUB_TABS.EXPORT)
                            }}
                            className={classNames('px-3 py-1.5 rounded-lg text-sm font-semibold whitespace-nowrap cursor-pointer', entityKey === key ? 'background text-family-700' : 'text-family-700 opacity-60')}>
                            {t(IMPORT_EXPORT_ENTITIES[key].label)}
                        </button>
                    ))}
                </div>
            </div>

            <div className="flex items-center gap-2">
                <button
                    type="button" onClick={() => setSubTab(SUB_TABS.EXPORT)}
                    className={classNames('px-3 py-1 rounded-full text-xs font-semibold cursor-pointer border', subTab === SUB_TABS.EXPORT ? 'bg-family-600 border-family-600 text-white' : 'border-color-200 text-color-400')}>
                    {t('export data')}
                </button>
                <button
                    type="button" onClick={() => setSubTab(SUB_TABS.IMPORT)}
                    className={classNames('px-3 py-1 rounded-full text-xs font-semibold cursor-pointer border', subTab === SUB_TABS.IMPORT ? 'bg-family-600 border-family-600 text-white' : 'border-color-200 text-color-400')}>
                    {t('import data')}
                </button>
            </div>

            {subTab === SUB_TABS.EXPORT
                ? <ExportTab key={entityKey} entity={entity} owner_id={owner_id} owner_type={owner_type} t={t}/>
                : <ImportTab key={entityKey} entity={entity} owner_id={owner_id} owner_type={owner_type} t={t}/>
            }
        </div>
    )
}

export default FamilyBookImportExportPage
